import { useEffect, useState } from "react";
import type { ChatMessage, ScheduledJob, SessionContact } from "../protocol";
import { unreadNotice } from "../unread";
import { middleEllipsis } from "../sessionTree";

/**
 * The header's "not read" line, kept current while the chat is open.
 *
 * A wait grows on its own, with nothing arriving to re-render it: the chat that has been sitting unread for
 * four minutes is the same data a minute later, when it has become five and there is something to say. So
 * the clock is read on a timer rather than only when a message lands. Coarsely — the line is in whole
 * minutes, so a slower tick would show a stale number and a faster one would re-render for nothing.
 */
function useUnreadNotice(messages: ChatMessage[]): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 20_000);
    return () => clearInterval(t);
  }, []);
  return unreadNotice(messages, now);
}

/** whenNext says when a job runs next: minutes ahead within the hour, else a time with its day. */
export function whenNext(ts: number, now: number): string {
  const mins = Math.round((ts - now) / 60_000);
  if (mins <= 0) return "due now";
  if (mins < 60) return `in ${mins} min`;
  const d = new Date(ts);
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const day = new Date(now);
  if (d.toDateString() === day.toDateString()) return `today ${time}`;
  day.setDate(day.getDate() + 1);
  if (d.toDateString() === day.toDateString()) return `tomorrow ${time}`;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${time}`;
}

/** SCHEDULE_FRESH_MS is how long the fleet's stamp vouches for a scheduled list. */
export const SCHEDULE_FRESH_MS = 15 * 60_000;

/**
 * The session's scheduled prompts: a header button with their count that opens the list, each deletable. A
 * list the machine's fleet has not confirmed lately is dimmed and says as of when it holds.
 */
function Scheduled({ jobs, seen, onCancel }: { jobs: ScheduledJob[]; seen: number; onCancel: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const now = Date.now();
  const stale = now - seen > SCHEDULE_FRESH_MS;
  return (
    <span className="sched">
      <button className="chat-head-action" title="Prompts scheduled for this session" onClick={() => setOpen(!open)}>
        ⏰ {jobs.length}
      </button>
      {open ? (
        <div className={`sched-pop${stale ? " stale" : ""}`} role="dialog" aria-label="Scheduled prompts">
          <div className="sched-title">
            Scheduled
            {stale ? (
              <span className="sched-asof">
                {seen ? `as of ${new Date(seen).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} · ` : ""}
                the machine's fleet is not answering
              </span>
            ) : null}
          </div>
          {jobs.map((j) => (
            <div key={j.id} className="sched-job">
              <div className="sched-when">
                <span>{j.schedule}</span>
                <span className="sched-next">next {whenNext(j.nextRun, now)}</span>
                <button className="sched-del" title="Delete this scheduled prompt" onClick={() => onCancel(j.id)}>
                  ✕
                </button>
              </div>
              <div className="sched-prompt">{j.prompt}</div>
              {j.recurring && j.expiresAt ? <div className="sched-expiry">ends {whenNext(j.expiresAt, now)}</div> : null}
            </div>
          ))}
        </div>
      ) : null}
    </span>
  );
}

/** The selected session's header: presence dot, name, a worktree's branch, live status line, and the archive action. */
export function ChatHeader({
  session,
  messages,
  onArchive,
  onNewAgent,
  onCancelScheduled,
}: {
  session: SessionContact | undefined;
  messages: ChatMessage[];
  onNewAgent?: () => void;
  onCancelScheduled?: (id: string) => void;
  onArchive: () => void;
}) {
  // Away = the server is up but no agent is attached. It outranks the live-activity line and suppresses
  // "awaiting your reply": with nobody listening, a pending question isn't surfaced here.
  const away = !!session?.online && session.agentAttached === false;
  // A message nobody has picked up outranks all of it — a session can read as active and still be sitting
  // on one, so the state of the agent is not the answer to the question the user is asking.
  const unread = useUnreadNotice(messages);
  return (
    <div className="chat-head">
      <span
        className={`dot${
          unread
            ? " stalled"
            : session?.online
              ? away
                ? " away"
                : session?.awaiting
                  ? " awaiting"
                  : session?.idle
                    ? " idle"
                    : " on"
              : session?.asleep
                ? " asleep"
                : ""
        }`}
      />
      <span className="chat-head-name">{session?.label ?? "Session"}</span>
      {session?.relationship === "child" && session.branch ? (
        <span className="row-branch chat-head-branch" title={session.branch}>
          {middleEllipsis(session.branch, 48)}
        </span>
      ) : null}
      <span
        className={`chat-head-status${
          unread ? " stalled" : session?.online && !away && session?.activityLine ? " activity" : ""
        }`}
      >
        {unread
          ? unread
          : !session?.online
            ? session?.asleep
              ? "asleep · a message wakes it"
              : session?.host === "conductor"
                ? "in Conductor · messages wait until you open it there"
                : "offline"
            : away
              ? "away"
              : session?.activityLine
                ? session.activityLine
                : session?.awaiting
                  ? "awaiting your reply"
                  : session?.idle
                    ? "idle"
                    : "active"}
      </span>
      {session?.scheduled?.length && onCancelScheduled ? <Scheduled jobs={session.scheduled} seen={session.scheduledSeen ?? 0} onCancel={onCancelScheduled} /> : null}
      {onNewAgent && session?.relationship !== "child" ? (
        <button className="chat-head-action" title="Have this session start a background agent" onClick={onNewAgent}>
          New agent
        </button>
      ) : null}
      <button className="chat-head-action" title="Archive this session" onClick={onArchive}>
        Archive
      </button>
    </div>
  );
}
